import { z } from 'zod';

/** Bounded machine observations. Never carries stderr, environment or host paths. */
export const ScannerExecutionSchema = z.object({
  phase: z.enum(['version', 'dependency', 'settings', 'check']),
  executable: z.enum(['ruff', 'python3', 'git-sizer', 'git']),
  version: z.string().max(64).optional(),
  exitCode: z.number().int().nullable(),
  signal: z
    .enum(['SIGTERM', 'SIGKILL', 'SIGABRT', 'SIGSEGV', 'other'])
    .nullable(),
  code: z.enum([
    'completed',
    'missing-executable',
    'missing-dependency',
    'timeout',
    'output-limit',
    'nonzero-exit',
    'invalid-output',
  ]),
  stderrBytes: z.number().int().nonnegative(),
  stdoutBytes: z.number().int().nonnegative(),
  diagnostic: z.enum([
    'none',
    'missing-git-object',
    'git-command-failed',
    'python-import-failed',
    'invalid-option',
    'unclassified',
  ]),
});
export type ScannerExecution = z.infer<typeof ScannerExecutionSchema>;

export const ScannerToolEvidenceSchema = z
  .object({
    excludedTools: z
      .array(
        z.enum([
          'suppression-audit',
          'lizard',
          'git-sizer',
          'vulture',
          'radon',
          'fallow',
        ]),
      )
      .max(6)
      .optional(),
    outcome: z.enum(['ok', 'missing', 'skipped', 'failed', 'unsupported']),
    reason: z.enum([
      'completed',
      'no-sources',
      'execution',
      'configuration',
      'source-coverage',
      'provenance-budget',
      'snapshot-preparation',
      'snapshot-cleanup',
    ]),
    executions: z.array(ScannerExecutionSchema).max(20),
    settings: z
      .array(
        z.object({
          configuration: z.string().max(512),
          extension: z.enum(['.py', '.pyi']),
          effective: z.string().max(32_768),
          // Configuration-selected set, before per-file ignores in `effective`.
          enabledRules: z.array(z.string().regex(/^[A-Z]+\d+$/u)).max(1000),
        }),
      )
      .max(16),
  })
  .superRefine((value, context) => {
    if (
      new TextEncoder().encode(JSON.stringify(value.settings)).byteLength >
      128 * 1024
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Tool settings exceed evidence budget',
      });
    }
  });
export type ScannerToolEvidence = z.infer<typeof ScannerToolEvidenceSchema>;

export const RUFF_PROFILE_PROMISE = {
  version: 'ruff-check-v1',
  tools: ['ruff'],
  rules:
    'Recorded configuration-selected rules, before per-file ignores retained in effective settings; configuration outside the supported subset remains unknown.',
  sources: 'git-tracked-python-v1',
  excludedTools: [
    'suppression-audit',
    'lizard',
    'git-sizer',
    'vulture',
    'radon',
    'fallow',
  ],
} as const;
